/**
 * Shared types and axis constants for the hip spine nomogram.
 */

import type {
    HipSpineSafeZone,
    NomogramMarker,
} from './hipSpineNomogramCalculations';

/** Extra degrees beyond MIN/MAX_INCLINATION to generate line data, for smooth rendering at chart edges. */
export const INCLINATION_RANGE_EXTENSION = 10;

/** A line on the nomogram defined by arrays of inclination and anteversion values */
export interface NomogramLine {
    inclination: number[];
    anteversion: number[];
}

/** Upper and lower CSI bound lines for nomogram rendering */
export interface CSIBounds {
    upper: NomogramLine;
    lower: NomogramLine;
}

export interface ClippedPoint {
    inclination: number;
    anteversion: number;
}

/**
 * Nomogram data ready for rendering.
 *
 * The safe zone is defined by the overlap (intersection) of the
 * inclination and anteversion angle range bands, clipped to the
 * area between the CSI upper and lower bound curves when available.
 */
export interface NomogramData {
    /** Customisable angle ranges that define the safe zone. Null when no hip spine data. */
    angleRanges: HipSpineSafeZone | null;
    marker: NomogramMarker | null;
    isMarkerInSafeZone: boolean;
    /** Combined version target line, transformed to the active alignment mode */
    combinedVersionTarget: NomogramLine | null;
    /** CSI upper (245°) and lower (205°) bound lines */
    csiBounds: CSIBounds;
    /** Pre-computed safe zone polygon (intersection of angle ranges and CSI bounds). Null when empty or no data. */
    safeZonePolygon: { inclination: number[]; anteversion: number[] } | null;
}
